import { Heading } from '@circlefin/components'
import { useMoney } from '@circlefin/hooks'
import { HistoryBackButton } from '@containers/layout'
import { TypeGuards } from '@services/type-guards'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Content } from '@shared/components/layout'
import { TransactionType } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { GrossRedemptionFeeCard, NetRedemptionFeeCard } from '../../components'

import type { ApolloError } from '@apollo/client'
import type { GrossRedemptionFee, NetRedemptionFee } from '@shared/graphql'

export interface RedemptionFeeDetailProps {
  /**
   * The transaction we want to render. Either a Gross Redemption Fee or Net Redemption Fee object.
   */
  transaction?: GrossRedemptionFee | NetRedemptionFee
  /**
   * Error from fetching transaction details.
   */
  error?: ApolloError
}

export const RedemptionFeeDetail: React.FC<RedemptionFeeDetailProps> = ({
  transaction,
  error,
}) => {
  const { t } = useTranslation()
  const { money } = useMoney()

  const isGrossRedemptionFee =
    TypeGuards.Payments.isPaymentDetails<TransactionType.redemption_fee_gross>(
      transaction,
      TransactionType.redemption_fee_gross,
    )

  const isNetRedemptionFee =
    TypeGuards.Payments.isPaymentDetails<TransactionType.redemption_fee_net>(
      transaction,
      TransactionType.redemption_fee_net,
    )

  return (
    <Content>
      <HistoryBackButton />
      <Heading variant="page/sm">
        <Heading.Eyebrow>{t`payments:redemptionFeeDetail.title`}</Heading.Eyebrow>
        <Heading.Header>
          {transaction?.payment.amount &&
            money({
              number: transaction.payment.amount.amount,
              variant: transaction.payment.amount.currency,
              options: {
                symbol: true,
                signDisplay: false,
              },
            })}
        </Heading.Header>
        <Heading.Intro>
          {t('payments:transactionDetail.transactionId', {
            id: transaction?.payment.id,
          })}
        </Heading.Intro>
      </Heading>

      <GraphQLErrorBoundary error={error}>
        {isGrossRedemptionFee && (
          <GrossRedemptionFeeCard payment={transaction.payment} />
        )}
        {isNetRedemptionFee && (
          <NetRedemptionFeeCard payment={transaction.payment} />
        )}
      </GraphQLErrorBoundary>
    </Content>
  )
}
